import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import { useSelector } from 'react-redux'
import { generatePath } from 'react-router-dom'
import styled from 'styled-components'

import {
  isAccountTypeRetailer,
  isAddOrCreateStylesRestricted,
  isLiteBrand,
  userCanViewDataPortal,
  userIsAdmin,
} from 'store/currentUser/selectors'

import {
  CREATE_LINESHEET,
  CREATE_STYLE,
  INVENTORY,
  LINESHEETS,
  NEW_CREATE_LINESHEET,
  SHOWROOM_COLLECTION_LIST,
  SHOWROOM_STYLEBOARD_LIST,
  SHOWROOM_TEMPLATE_LIST,
  STAGING,
  STYLES,
  STYLES_BULK_IMAGE_UPLOADER,
  STYLE_SYNC,
  SWATCH_MANAGEMENT,
  UPLOAD_DATA,
} from 'routes/paths'

import { IProductsDropdownSelectors } from '../../navbarRevamp.types'
import DropdownMenuGroup from '../DropdownMenuGroup/DropdownMenuGroup'
import DropdownMenuTextLink from '../DropdownMenuTextLink/DropdownMenuTextLink'
import NavbarDropdown from '../NavbarDropdown/NavbarDropdown'
import Trigger from '../Trigger/Trigger'
import {
  useBulkImageUploaderExperience,
  useGetBrandStyleIdentifier,
  useImportStylesExperience,
  useSetPendoIds,
} from './navbarProductsDropdown.hooks'
import messages from 'features/Navbar/NavbarRevamp/constants/navbarRevamp.messages'
import { ProductsDropdownIds } from 'features/Navbar/NavbarRevamp/navbarRevamp.ids'
import {
  IMPORT_STYLES_KEY_PARAM,
  IMPORT_STYLES_VALUE_PARAM,
} from 'features/Styles/constants/constants'

const NavbarProductsDropdownContainer = styled.div`
  display: flex;
`

interface LinesheetGroupProps {
  newCollectionsEditAndCreate: boolean
  canViewDataPortal: boolean
  isLiteBrandAccount: boolean
}

const LinesheetGroup = ({
  newCollectionsEditAndCreate,
  canViewDataPortal,
  isLiteBrandAccount,
}: LinesheetGroupProps) => (
  <DropdownMenuGroup>
    <DropdownMenuTextLink
      to={LINESHEETS}
      message={messages.manageLinesheets}
      id={ProductsDropdownIds.ProductsOptionsManageLinesheets}
      testId={ProductsDropdownIds.ProductsOptionsManageLinesheets}
    />
    {!isLiteBrandAccount && (
      <DropdownMenuTextLink
        to={
          newCollectionsEditAndCreate ? NEW_CREATE_LINESHEET : CREATE_LINESHEET
        }
        message={messages.createLinesheet}
        id={ProductsDropdownIds.ProductsOptionsCreateLinesheet}
        testId={ProductsDropdownIds.ProductsOptionsCreateLinesheet}
      />
    )}

    {canViewDataPortal && (
      <DropdownMenuTextLink
        to={UPLOAD_DATA}
        message={messages.uploadData}
        id={ProductsDropdownIds.ProductsOptionsUploadData}
        testId={ProductsDropdownIds.ProductsOptionsUploadData}
      />
    )}
  </DropdownMenuGroup>
)
interface StylesGroupProps {
  restrictAddOrCreateStyles: boolean
  stylesPath: string
  isLiteBrandAccount: boolean
  swatchCreationAndManagement: boolean
  isRetailerAccount: boolean
}

const StylesGroup = ({
  restrictAddOrCreateStyles,
  stylesPath,
  isLiteBrandAccount,
  swatchCreationAndManagement,
  isRetailerAccount,
}: StylesGroupProps) => {
  const { styleIdentifiers, loading: loadingStyleIdentifier } =
    useGetBrandStyleIdentifier(isRetailerAccount)

  const {
    isEnabled: isImportStylesEnabled,
    loading: loadingImportStylesExperience,
  } = useImportStylesExperience(styleIdentifiers, loadingStyleIdentifier)

  const isBulkImageUploaderEnabled = useBulkImageUploaderExperience()

  useSetPendoIds(
    isBulkImageUploaderEnabled,
    isImportStylesEnabled,
    loadingStyleIdentifier || loadingImportStylesExperience,
  )

  return (
    <DropdownMenuGroup>
      <DropdownMenuTextLink
        to={stylesPath}
        message={messages.manageStyles}
        id={ProductsDropdownIds.ProductsOptionsManageStyles}
        testId={ProductsDropdownIds.ProductsOptionsManageStyles}
      />
      {!restrictAddOrCreateStyles && !isLiteBrandAccount && (
        <DropdownMenuTextLink
          to={CREATE_STYLE}
          message={messages.createStyle}
          id={ProductsDropdownIds.ProductsOptionsCreateStyle}
          testId={ProductsDropdownIds.ProductsOptionsCreateStyle}
        />
      )}
      {isImportStylesEnabled && !loadingImportStylesExperience && (
        <DropdownMenuTextLink
          to={`${STYLES}?${IMPORT_STYLES_KEY_PARAM}=${IMPORT_STYLES_VALUE_PARAM}`}
          message={messages.uploadStyles}
          id={ProductsDropdownIds.ProductsOptionsBasicStyleUploader}
          testId={ProductsDropdownIds.ProductsOptionsBasicStyleUploader}
        />
      )}
      {isBulkImageUploaderEnabled && (
        <DropdownMenuTextLink
          to={STYLES_BULK_IMAGE_UPLOADER}
          message={messages.uploadImages}
          id={ProductsDropdownIds.ProductsOptionsUploadImages}
          testId={ProductsDropdownIds.ProductsOptionsUploadImages}
        />
      )}
      {swatchCreationAndManagement && (
        <DropdownMenuTextLink
          to={generatePath(SWATCH_MANAGEMENT)}
          message={messages.colorSwatch}
          id={ProductsDropdownIds.ProductsOptionsColorSwatches}
          testId={ProductsDropdownIds.ProductsOptionsColorSwatches}
        />
      )}
    </DropdownMenuGroup>
  )
}

const InventoryGroup = () => (
  <DropdownMenuGroup>
    <DropdownMenuTextLink
      to={INVENTORY}
      message={messages.inventory}
      id={ProductsDropdownIds.ProductsOptionsInventory}
      testId={ProductsDropdownIds.ProductsOptionsInventory}
    />
  </DropdownMenuGroup>
)

const LooksGroup = () => (
  <DropdownMenuGroup>
    <DropdownMenuTextLink
      to={SHOWROOM_COLLECTION_LIST}
      message={messages.manageLooks}
      id={ProductsDropdownIds.ProductsOptionsManageLooks}
      testId={ProductsDropdownIds.ProductsOptionsManageLooks}
    />
    <DropdownMenuTextLink
      to={SHOWROOM_TEMPLATE_LIST}
      message={messages.createLook}
      id={ProductsDropdownIds.ProductsOptionsCreateLook}
      testId={ProductsDropdownIds.ProductsOptionsCreateLook}
    />
  </DropdownMenuGroup>
)

const StyleboardsGroup = () => (
  <DropdownMenuGroup>
    <DropdownMenuTextLink
      to={SHOWROOM_STYLEBOARD_LIST}
      message={messages.manageStyleboards}
      id={ProductsDropdownIds.ProductsOptionsManageStyleboards}
      testId={ProductsDropdownIds.ProductsOptionsManageStyleboards}
    />
  </DropdownMenuGroup>
)

const StagingSyncGroup = () => (
  <DropdownMenuGroup>
    <DropdownMenuTextLink
      to={STAGING}
      message={messages.staging}
      id={ProductsDropdownIds.ProductsOptionsManageStaging}
      testId={ProductsDropdownIds.ProductsOptionsManageStaging}
    />
    <DropdownMenuTextLink
      to={STYLE_SYNC}
      message={messages.styleSync}
      id={ProductsDropdownIds.ProductsOptionsManageStyleSync}
      testId={ProductsDropdownIds.ProductsOptionsManageStyleSync}
    />
  </DropdownMenuGroup>
)

const NavbarProductsDropdown = () => {
  const {
    canViewDataPortal,
    restrictAddOrCreateStyles,
    isRetailerAccount,
    isLiteBrandAccount,
    isAdmin,
  } = useSelector<object, IProductsDropdownSelectors>((state) => ({
    canViewDataPortal: userCanViewDataPortal(state),
    restrictAddOrCreateStyles: isAddOrCreateStylesRestricted(state),
    isRetailerAccount: isAccountTypeRetailer(state),
    isLiteBrandAccount: isLiteBrand(state),
    isAdmin: userIsAdmin(state),
  }))

  const {
    newCollectionsEditAndCreate,
    virtualShowroom,
    swatchCreationAndManagement,
  } = useFlags()

  if (isRetailerAccount) {
    return null
  }

  return (
    <NavbarProductsDropdownContainer>
      <NavbarDropdown
        trigger={
          <Trigger
            label={messages.products}
            id={ProductsDropdownIds.ProductsTrigger}
          />
        }
      >
        <LinesheetGroup
          canViewDataPortal={canViewDataPortal}
          newCollectionsEditAndCreate={newCollectionsEditAndCreate}
          isLiteBrandAccount={isLiteBrandAccount}
        />
        <StylesGroup
          restrictAddOrCreateStyles={restrictAddOrCreateStyles}
          stylesPath={STYLES}
          isLiteBrandAccount={isLiteBrandAccount}
          swatchCreationAndManagement={swatchCreationAndManagement}
          isRetailerAccount={isRetailerAccount}
        />
        {!isLiteBrandAccount && <InventoryGroup />}
        {virtualShowroom && <LooksGroup />}
        {virtualShowroom && <StyleboardsGroup />}
        {isAdmin && <StagingSyncGroup />}
      </NavbarDropdown>
    </NavbarProductsDropdownContainer>
  )
}

export default NavbarProductsDropdown
